import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Clock, CheckCircle2, AlertTriangle, Loader2 } from 'lucide-react';
import SendReceiptDialog from './SendReceiptDialog';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const POLL_INTERVAL_MS = 10_000;
// How long to keep the "approved" / "rejected" banner around once the
// decision is made so the player has time to see it.
const SHOW_DECIDED_FOR_MS = 90_000;
const TYPICAL_APPROVE_SECONDS = 60;

const fmtMoney = (n) => `$${Number(n || 0).toFixed(2)}`;

/**
 * Lobby home banner that shows the live status of the player's most recent
 * Square receipt submission, with a soft "you'll see your credits in ~60s"
 * countdown while pending. Designed to turn the silent wait between upload
 * and credit into a visible, measurable promise.
 *
 * Hides itself when there's nothing relevant — no clutter when the player
 * hasn't uploaded anything.
 */
const ReceiptCountdownBanner = () => {
    const [row, setRow] = useState(null);
    const [now, setNow] = useState(() => Date.now());
    const [resendOpen, setResendOpen] = useState(false);

    const fetchLatest = useCallback(async () => {
        try {
            const { data } = await axios.get(`${API}/square-receipts/mine?limit=1`,
                { withCredentials: true });
            const next = (data.rows && data.rows[0]) || null;
            // Fire the gold "ka-ching" balance flash the first time we see the
            // latest pending receipt flip to approved — only here so it lands
            // exactly once per credit.
            setRow((prev) => {
                if (
                    next && prev && next.id === prev.id &&
                    prev.status === 'pending' && next.status === 'approved'
                ) {
                    const amt = Number(next.credit_amount || next.ocr_amount || 0);
                    if (amt > 0) {
                        try {
                            window.dispatchEvent(new CustomEvent('betohalics:credits-jackpot',
                                { detail: { amount: amt } }));
                        } catch { /* ignore */ }
                    }
                }
                return next;
            });
        } catch { /* hide silently */ }
    }, []);

    // Initial fetch + 10s polling
    useEffect(() => {
        fetchLatest();
        const id = setInterval(fetchLatest, POLL_INTERVAL_MS);
        return () => clearInterval(id);
    }, [fetchLatest]);

    // 1s "now" tick — only ticks while there's a pending row visible
    useEffect(() => {
        if (!row || row.status !== 'pending') return undefined;
        const id = setInterval(() => setNow(Date.now()), 1000);
        return () => clearInterval(id);
    }, [row]);

    if (!row) return null;
    const status = row.status;
    const amount = row.credit_amount || row.ocr_amount || row.claimed_amount || 0;

    // Auto-hide approved / rejected after 90 s
    if (status === 'approved' || status === 'rejected') {
        const decidedAt = row.decided_at ? new Date(row.decided_at).getTime() : 0;
        if (!decidedAt || (Date.now() - decidedAt) > SHOW_DECIDED_FOR_MS) return null;
    }

    if (status === 'pending') {
        const createdMs = row.created_at ? new Date(row.created_at).getTime() : Date.now();
        const elapsed = Math.max(0, Math.floor((now - createdMs) / 1000));
        const remaining = Math.max(0, TYPICAL_APPROVE_SECONDS - elapsed);
        const percent = Math.min(99, Math.round((elapsed / TYPICAL_APPROVE_SECONDS) * 100));

        let label;
        if (remaining > 0) {
            label = `You'll see your credits in ~${remaining}s`;
        } else if (elapsed < 180) {
            label = "Any second now — staff are looking…";
        } else {
            label = "Taking a little longer than usual — staff are on it";
        }

        return (
            <div
                className="rounded-2xl border border-amber-500/40 bg-gradient-to-br from-amber-500/15 via-[#0a0a0a] to-[#0a0a0a] p-4 sm:p-5"
                data-testid="receipt-countdown-banner"
                data-status="pending"
            >
                <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div className="flex items-center gap-3 min-w-0">
                        <div className="w-10 h-10 rounded-full bg-amber-500/20 border border-amber-500/40 flex items-center justify-center shrink-0">
                            <Loader2 className="w-5 h-5 text-amber-300 animate-spin" />
                        </div>
                        <div className="min-w-0">
                            <div className="text-[10px] uppercase tracking-widest text-amber-300 font-black">
                                Receipt received · pending review
                            </div>
                            <div className="text-white font-black text-base sm:text-lg mt-0.5 truncate">
                                {label}
                            </div>
                            <div className="text-[11px] text-zinc-400 mt-0.5">
                                Payment · {fmtMoney(amount)} on its way
                            </div>
                        </div>
                    </div>
                </div>
                <div className="mt-3 h-1.5 rounded-full bg-amber-500/10 overflow-hidden">
                    <div
                        className="h-full bg-amber-400 transition-all duration-1000 ease-linear"
                        style={{ width: `${percent}%` }}
                    />
                </div>
            </div>
        );
    }

    if (status === 'approved') {
        return (
            <div
                className="rounded-2xl border border-emerald-500/40 bg-gradient-to-br from-emerald-500/15 via-[#0a0a0a] to-[#0a0a0a] p-4 sm:p-5"
                data-testid="receipt-countdown-banner"
                data-status="approved"
            >
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-emerald-500/20 border border-emerald-500/40 flex items-center justify-center shrink-0">
                        <CheckCircle2 className="w-5 h-5 text-emerald-300" />
                    </div>
                    <div>
                        <div className="text-[10px] uppercase tracking-widest text-emerald-300 font-black">
                            Credited!
                        </div>
                        <div className="text-white font-black text-base sm:text-lg mt-0.5">
                            {fmtMoney(amount)} added to your wallet
                        </div>
                        <div className="text-[11px] text-zinc-400 mt-0.5">
                            Happy playing — good luck out there.
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (status === 'rejected') {
        return (
            <>
                <button
                    type="button"
                    onClick={() => setResendOpen(true)}
                    className="w-full text-left rounded-2xl border border-rose-500/40 bg-gradient-to-br from-rose-500/15 via-[#0a0a0a] to-[#0a0a0a] p-4 sm:p-5 hover:border-rose-400 transition-colors"
                    data-testid="receipt-countdown-banner"
                    data-status="rejected"
                >
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-rose-500/20 border border-rose-500/40 flex items-center justify-center shrink-0">
                            <AlertTriangle className="w-5 h-5 text-rose-300" />
                        </div>
                        <div>
                            <div className="text-[10px] uppercase tracking-widest text-rose-300 font-black">
                                Couldn&apos;t verify your receipt
                            </div>
                            <div className="text-white font-black text-base sm:text-lg mt-0.5">
                                Tap here to send a new screenshot
                            </div>
                            {row.reject_reason && (
                                <div className="text-[11px] text-zinc-400 mt-0.5">
                                    Reason: {row.reject_reason}
                                </div>
                            )}
                        </div>
                    </div>
                </button>
                <SendReceiptDialog open={resendOpen} onClose={() => setResendOpen(false)} />
            </>
        );
    }

    return null;
};

export default ReceiptCountdownBanner;
